"use client";

import { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import { renderPdfPage } from '@/lib/pdf-editor/pdfLoader';
import { useEditorStore } from '@/lib/pdf-editor/store';

interface PdfPageProps {
  pdfDoc: any; // pdfjs-dist PDFDocumentProxy
  pageNumber: number;
}

export default function PdfPage({ pdfDoc, pageNumber }: PdfPageProps) {
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<HTMLCanvasElement>(null);
  const fabricInstance = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { zoom, activeTool, registerCanvas, unregisterCanvas, setSelectedObject } = useEditorStore();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // 1. Render PDF Layer
  useEffect(() => {
    if (!pdfDoc || !pdfCanvasRef.current) return;

    const render = async () => {
      const result = await renderPdfPage(pdfDoc, pageNumber, pdfCanvasRef.current!, zoom);
      if (result && result.viewport) {
        const { viewport } = result;
        setDimensions({ width: viewport.width, height: viewport.height });
        
        // Resize fabric canvas if it exists
        if (fabricInstance.current) {
          fabricInstance.current.setDimensions({ 
            width: viewport.width, 
            height: viewport.height 
          });
          fabricInstance.current.setZoom(zoom);
        }
      }
    };
    
    render();
  }, [pdfDoc, pageNumber, zoom]);

  // 2. Initialize Fabric Layer
  useEffect(() => {
    if (!fabricCanvasRef.current || fabricInstance.current) return;

    // Create Fabric Canvas
    const canvas = new fabric.Canvas(fabricCanvasRef.current, {
      width: dimensions.width,
      height: dimensions.height,
      selection: true, // Enable group selection
      preserveObjectStacking: true,
    });

    fabricInstance.current = canvas;
    registerCanvas(pageNumber, canvas);

    // Event Listeners
    canvas.on('selection:created', (e) => {
      const active = e.selected?.[0];
      if (active) setSelectedObject(active.type === 'activeSelection' ? null : 'active');
    });
    
    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
    });

    return () => {
      canvas.dispose();
      fabricInstance.current = null;
      unregisterCanvas(pageNumber);
    };
  }, [pageNumber, registerCanvas, unregisterCanvas, setSelectedObject]); // Only init once, resize later

  // 3. Update Fabric Tool/Mode
  useEffect(() => {
    const canvas = fabricInstance.current;
    if (!canvas) return;

    canvas.isDrawingMode = activeTool === 'draw' || activeTool === 'highlighter';
    
    if (activeTool === 'draw') {
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = 'black';
      canvas.freeDrawingBrush.width = 2;
    } else if (activeTool === 'highlighter') {
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = 'rgba(255, 255, 0, 0.5)'; // Transparent yellow
      canvas.freeDrawingBrush.width = 20;
    }

    // Make objects selectable only in select mode
    canvas.forEachObject((obj) => {
      obj.selectable = activeTool === 'select';
      obj.evented = activeTool === 'select';
    });
    
    canvas.requestRenderAll();
  }, [activeTool]);

  return (
    <div ref={containerRef} className="relative mb-8 shadow-lg bg-white" style={{ width: dimensions.width, height: dimensions.height }}>
      {/* PDF Layer (Background) */}
      <canvas 
        ref={pdfCanvasRef} 
        className="absolute inset-0 z-0 pointer-events-none"
      />
      
      {/* Fabric Layer (Interactive) */}
      <div className="absolute inset-0 z-10">
        <canvas ref={fabricCanvasRef} />
      </div>
    </div>
  );
}
